<template>
    <div
        v-if="isLoadingMatch || isRestoring"
        class="flex h-dvh w-full items-center justify-center bg-black"
    >
        <div class="text-center">
            <div class="mb-4 text-white">
                {{ isRestoring ? $t('play.restoring') : $t('play.loading') }}
            </div>
            <div
                class="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-red-500 border-t-transparent"
            ></div>
        </div>
    </div>
    <template v-else>
        <PageHeader
            v-if="currentGameComponent !== 'CamelraceGame'"
            :title="currentGameComponent !== 'KillerGame' ? gameTitle : ''"
            :transparent="currentGameComponent === 'KillerGame'"
            :is-loading="isQuitting"
            @back="quitMatch"
        >
            <template v-if="currentGameComponent === 'KillerGame'" #right-content>
                <button
                    @click="openKillerInfo"
                    class="flex items-center justify-center text-sm text-white opacity-70"
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M16 9.00109C16 3.62399 10.1679 0.257799 5.49902 2.94934C3.33318 4.19805 2 6.50538 2 9.00109C2.00003 14.3783 7.83208 17.7444 12.501 15.0529C14.6668 13.8041 16 11.4969 16 9.00109ZM10 7.00109V14.0011H8V7.00109H10ZM10 4.00109V5.50109H8V4.00109H10ZM18 9.00109C18 12.2129 16.2836 15.18 13.499 16.7853C7.50132 20.2426 2.82521e-05 15.9224 0 9.00109C0 5.7893 1.71642 2.82219 4.50098 1.21691C10.4987 -2.2404 18 2.07989 18 9.00109Z"
                            fill="white"
                        />
                    </svg>
                </button>
            </template>
        </PageHeader>
        <component :is="currentGameComponent" @quit-match="quitMatch" @exit="handleExit" />
    </template>

    <MatchOverModal
        :is-visible="isMatchOverModalVisible"
        :match-data="matchData"
        :is-rematch-loading="isRematchLoading"
        @rematch="handleRematch"
        @show-game-stats="handleShowGameStats"
        v-if="currentGameComponent !== 'CamelraceGame'"
    />
    <CamelMatchOverModal
        :is-visible="isMatchOver"
        :teams="teams"
        :profiles="profiles"
        :is-rematch-loading="isRematchLoading"
        @rematch="handleRematch"
        @show-game-stats="handleShowGameStats"
        @new-game="handleNewGame"
        @exit="handleExit"
        v-else
    />

    <!-- Leg Won Notification -->
    <div v-if="showLegWonNotification" class="fixed inset-0 z-50 flex items-center justify-center">
        <div class="absolute inset-0 z-40 bg-black/65"></div>
        <div class="bg-bg border-stroke-1 relative z-50 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div class="mb-6 text-center">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('play.leg_won_title') }}
                    </h2>
                    <p class="text-sm text-gray-300">{{ $t('play.leg_won_body') }}</p>
                </div>

                <div class="space-y-6">
                    <div v-if="legWinnerTeam" class="border border-green-500 bg-green-600 p-4">
                        <div class="space-y-3">
                            <h3
                                v-if="legWinnerTeam.players.length > 1"
                                class="mb-3 text-xl font-bold text-white uppercase"
                            >
                                {{ legWinnerTeam.name }}
                            </h3>

                            <div class="flex flex-col gap-2">
                                <div
                                    v-for="player in legWinnerTeam.players"
                                    :key="player.id"
                                    class="flex items-center justify-between"
                                >
                                    <div class="flex items-center space-x-2">
                                        <Avatar
                                            :size="12"
                                            :src="player.avatar"
                                            :alt="player.tag_name"
                                        />
                                        <span class="text-sm font-medium text-white">
                                            {{ player.tag_name }}
                                        </span>
                                    </div>
                                    <div class="text-2xl">🏆</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-else-if="legWinnerDisplayName"
                        class="border border-green-500 bg-green-600 p-4"
                    >
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-2">
                                <Avatar
                                    :size="12"
                                    :src="legWinnerProfile?.avatar"
                                    :alt="legWinnerDisplayName"
                                />
                                <span class="text-sm font-medium text-white">
                                    {{ legWinnerDisplayName }}
                                </span>
                            </div>
                            <div class="text-2xl">🏆</div>
                        </div>
                    </div>
                </div>
                <div class="mt-6 text-center">
                    <p class="text-sm text-gray-300">
                        {{ $t('play.next_leg_countdown', { n: countdownValue }) }}
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- Set Won Notification -->
    <div v-if="showSetWonNotification" class="fixed inset-0 z-50 flex items-center justify-center">
        <div class="absolute inset-0 z-40 bg-black/65"></div>
        <div class="bg-bg border-stroke-1 relative z-50 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div class="mb-6 text-center">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('play.set_won_title') }}
                    </h2>
                    <p class="text-sm text-gray-300">{{ $t('play.set_won_body') }}</p>
                </div>

                <div class="space-y-6">
                    <div v-if="setWinnerTeam" class="border border-green-500 bg-green-600 p-4">
                        <div class="space-y-3">
                            <h3
                                v-if="setWinnerTeam.players.length > 1"
                                class="mb-3 text-xl font-bold text-white uppercase"
                            >
                                {{ setWinnerTeam.name }}
                            </h3>

                            <div class="flex flex-col gap-2">
                                <div
                                    v-for="player in setWinnerTeam.players"
                                    :key="player.id"
                                    class="flex items-center justify-between"
                                >
                                    <div class="flex items-center space-x-2">
                                        <Avatar
                                            :size="12"
                                            :src="player.avatar"
                                            :alt="player.tag_name"
                                        />
                                        <span class="text-sm font-medium text-white">
                                            {{ player.tag_name }}
                                        </span>
                                    </div>
                                    <div class="text-2xl">🏆</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-else-if="setWinnerDisplayName"
                        class="border border-green-500 bg-green-600 p-4"
                    >
                        <div class="flex items-center justify-between">
                            <div class="flex items-center space-x-2">
                                <Avatar
                                    :size="12"
                                    :src="setWinnerProfile?.avatar"
                                    :alt="setWinnerDisplayName"
                                />
                                <span class="text-sm font-medium text-white">
                                    {{ setWinnerDisplayName }}
                                </span>
                            </div>
                            <div class="text-2xl">🏆</div>
                        </div>
                    </div>
                </div>

                <div class="mt-6 text-center">
                    <p class="text-sm text-gray-300">
                        {{ $t('play.next_set_countdown', { n: setCountdownValue }) }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRoute } from 'vue-router'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth'
import { useVisibilityRestore } from '@/composables/useVisibilityRestore.js'
import { useErrorReporting } from '@/composables/useErrorReporting.js'
import PageHeader from '@/components/PageHeader.vue'
import MatchOverModal from '@/components/MatchOverModal.vue'
import Avatar from '@/components/Avatar.vue'
import X01Game from '@/components/games/X01Game.vue'
import CricketGame from '@/components/games/CricketGame.vue'
import KillerGame from '@/components/games/KillerGame.vue'
import CamelraceGame from '@/components/games/CamelraceGame.vue'
import SegmentsTrainingGame from '@/components/games/SegmentsTrainingGame.vue'
import CamelMatchOverModal from '@/components/games/camelrace/MatchOverModal.vue'
import { MATCH_TYPES, MATCH_TYPE_NAMES, matchTypeName } from '@/constants/matchTypes.js'

export default {
    name: 'PlayView',
    components: {
        PageHeader,
        MatchOverModal,
        CamelMatchOverModal,
        Avatar,
        X01Game,
        CricketGame,
        KillerGame,
        CamelraceGame,
        SegmentsTrainingGame,
    },
    setup() {
        const route = useRoute()
        const { isRestoring } = useVisibilityRestore(() => route.params.matchId)
        const errorReporting = useErrorReporting()
        const authStore = useAuthStore()
        return { isRestoring, errorReporting, authStore }
    },
    data() {
        return {
            showLegWonNotification: false,
            showSetWonNotification: false,
            legWinnerId: null,
            isLoadingMatch: false,
            isRematchLoading: false,
            isQuitting: false,
            countdownValue: 3,
            setCountdownValue: 3,
            countdownInterval: null,
            setCountdownInterval: null,
            setWinnerId: null,
            countdownDepth: 0,
        }
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        currentPlayer() {
            return this.matchStore.getCurrentPlayer
        },
        currentMatchTypeId() {
            return this.matchStore.getMatchTypeId
        },
        currentGameComponent() {
            const matchTypeId = this.currentMatchTypeId

            if (matchTypeId === MATCH_TYPES.X01) {
                return 'X01Game'
            } else if (matchTypeId === MATCH_TYPES.CRICKET) {
                return 'CricketGame'
            } else if (matchTypeId === MATCH_TYPES.KILLER) {
                return 'KillerGame'
            } else if (matchTypeId === MATCH_TYPES.CAMELRACE) {
                return 'CamelraceGame'
            } else if (matchTypeId === MATCH_TYPES.SEGMENTS) {
                return 'SegmentsTrainingGame'
            }

            return null
        },
        gameTitle() {
            const gameName =
                MATCH_TYPE_NAMES[this.currentMatchTypeId] || this.$t('play.game_loading')

            return `${gameName}`
        },
        isMatchOver() {
            return this.matchStore.isMatchOver
        },
        isMatchOverModalVisible() {
            return this.isMatchOver && this.currentGameComponent !== 'KillerGame'
        },
        matchEndedRemotely() {
            return this.matchStore.matchEndedRemotely
        },
        matchData() {
            return this.matchStore.getMatchData
        },
        currentLegCount() {
            const currentSet = this.matchData?.sets?.[this.matchData.sets.length - 1]
            return currentSet?.legs?.length || 0
        },
        currentSetCount() {
            return this.matchData?.sets?.length || 0
        },
        profiles() {
            return this.matchStore.getProfiles
        },
        teams() {
            return this.matchStore.getTeams
        },
        isTeamPlay() {
            return this.matchStore.isTeamPlay
        },
        legWinnerTeam() {
            if (!this.legWinnerId) return null
            return this.teams.find((team) => team.id === this.legWinnerId)
        },
        legWinnerProfile() {
            if (!this.legWinnerId) return null
            const team = this.teams.find((t) => t.id === this.legWinnerId)
            if (team?.players?.length === 1) {
                return team.players[0]
            }
            return this.profiles.find((p) => p.team === this.legWinnerId)
        },
        legWinnerDisplayName() {
            if (!this.legWinnerId) return ''
            const team = this.teams.find((t) => t.id === this.legWinnerId)
            if (team) {
                return team.players && team.players.length > 1
                    ? team.name
                    : team.players?.[0]?.tag_name || ''
            }
            const profile = this.profiles.find((p) => p.team === this.legWinnerId)
            return profile?.tag_name || ''
        },
        setWinnerTeam() {
            if (!this.setWinnerId) return null
            return this.teams.find((team) => team.id === this.setWinnerId)
        },
        setWinnerProfile() {
            if (!this.setWinnerId) return null
            const team = this.teams.find((t) => t.id === this.setWinnerId)
            if (team?.players?.length === 1) {
                return team.players[0]
            }
            return this.profiles.find((p) => p.team === this.setWinnerId)
        },
        setWinnerDisplayName() {
            if (!this.setWinnerId) return ''
            const team = this.teams.find((t) => t.id === this.setWinnerId)
            if (team) {
                return team.players && team.players.length > 1
                    ? team.name
                    : team.players?.[0]?.tag_name || ''
            }
            const profile = this.profiles.find((p) => p.team === this.setWinnerId)
            return profile?.tag_name || ''
        },
    },
    watch: {
        matchEndedRemotely(info) {
            if (info) {
                this.handleRemoteMatchEnd(info)
            }
        },
        currentMatchTypeId(newVal) {
            console.log('Match type changed, clearing editing turn')
            if (newVal) {
                this.matchStore.clearEditingTurn()
                // The match type can arrive after mount (API restore, realtime rejoin); report the
                // screen as soon as it is known. Repeats of the same screen name are ignored.
                this.trackGameScreen()
            }
        },
        currentLegCount(newCount, oldCount) {
            if (oldCount && newCount > oldCount && this.matchData?.sets) {
                const currentSet = this.matchData.sets[this.matchData.sets.length - 1]

                if (currentSet?.legs?.length > 1) {
                    const finishedLeg = currentSet.legs[currentSet.legs.length - 2]
                    this.legWinnerId = finishedLeg?.winner_id || null
                    this.showLegWonMessage()
                }
            }
        },
        currentSetCount(newCount, oldCount) {
            if (oldCount && newCount > oldCount) {
                this.showSetWonMessage()
            }
        },
    },
    async mounted() {
        const matchId = this.$route.params.matchId
        const alreadyHasMatchData =
            matchId &&
            (this.matchStore.setup.id === matchId || this.matchStore.match.id === matchId)

        // Persisted match state can be stale (e.g. after a browser refresh, or
        // when the realtime channel died while the tab was backgrounded), so
        // always resync with the server and (re)join the realtime channel
        // unless we already have a live channel for this exact match.
        if (matchId && (!alreadyHasMatchData || !this.matchStore.activeChannel)) {
            this.isLoadingMatch = !alreadyHasMatchData
            try {
                await this.matchStore.restoreMatchFromAPI(matchId)
            } catch (error) {
                console.error('Failed to restore match:', error)
                this.errorReporting.captureError(error, {
                    tags: {
                        component: 'PlayView',
                        action: 'restoreMatch',
                        matchId: String(matchId),
                    },
                    extra: {
                        route: this.$route.path,
                        matchStoreState: {
                            setupId: this.matchStore.setup.id,
                            matchId: this.matchStore.match.id,
                        },
                    },
                })
                const isNotFound = error.response?.status === 404 || error.message?.includes('404')
                if (isNotFound) {
                    this.$router.replace({
                        name: 'home',
                        query: { matchNotFound: 'true' },
                    })
                } else {
                    this.$router.replace({ name: 'games' })
                }
                return
            } finally {
                this.isLoadingMatch = false

                // Local play is scored on the creator's device only. Online
                // matches are played by every participant on their own device.
                if (
                    !this.matchStore.isRemotePlay &&
                    this.matchStore.match.creator_profile_id !== this.authStore.profile?.id
                ) {
                    this.$router.replace('/')
                }
            }
        }

        this.trackGameScreen()
    },
    methods: {
        // The router skips screen_view for game routes so the event can carry the game type.
        trackGameScreen() {
            const matchId = this.$route.params.matchId ?? this.matchStore.setup?.id
            this.$analytics.trackScreen('Game On - Game', {
                game_type: matchTypeName(this.currentMatchTypeId, 'Unknown'),
                game_context: this.matchStore.isRemotePlay ? 'online' : 'local',
                // Game routes skip trackPageView, so match_id has to be attached here for the
                // in-game events to inherit it as a sticky param.
                ...(matchId ? { match_id: String(matchId) } : {}),
                route_name: this.$route.name,
                path: this.$route.fullPath,
            })
        },
        openKillerInfo() {
            this.$serviceBus.emit('open-game-explanation', {
                key: 'killer',
                infoOnly: true,
            })
        },
        handleRemoteMatchEnd(info) {
            if (this.isQuitting) return
            this.isQuitting = true

            const leaverName = info?.endedBy || this.$t('play.a_player')

            this.matchStore.clearMatchEndedRemotely()
            this.matchStore.clearMatch()

            this.$toast(
                this.$t('play.match_ended_title'),
                6000,
                this.$t('play.match_ended_body', { name: leaverName }),
            )

            this.$router.push({ name: 'home' })
        },
        async quitMatch() {
            if (this.isQuitting) return

            console.log('Quitting match')
            this.isQuitting = true
            const matchId = this.matchStore.match.id

            try {
                if (matchId) {
                    await this.matchStore.cancelMatch(matchId)
                }

                this.$router.push({ name: 'home' })
            } catch (error) {
                console.error('Failed to quit match:', error)
                this.errorReporting.captureError(error, {
                    tags: {
                        component: 'PlayView',
                        action: 'quitMatch',
                        matchId: String(matchId),
                    },
                    extra: {
                        route: this.$route.path,
                    },
                })
                this.$toast(this.$t('general.error'), 5000, this.$t('general.error_retry'))
            } finally {
                this.isQuitting = false
            }
        },
        async handleRematch() {
            if (this.isRematchLoading) return

            console.log('Rematch requested')
            this.isRematchLoading = true

            try {
                const match = await this.matchStore.rematch(this.$route.params.matchId)

                const isSoloGame = this.matchStore.getProfiles.length === 1

                if (isSoloGame) {
                    const soloPlayer = this.matchStore.getProfiles[0]
                    this.matchStore.setup.starting_player = soloPlayer.team

                    await this.matchStore.startMatch()
                    this.$router.push({
                        name: 'match-play',
                        params: { matchId: match.id },
                    })
                } else {
                    this.$router.push({
                        name: 'match-starter-selector',
                        params: { matchId: match.id },
                        query: { rematch: true },
                    })
                }
            } catch (error) {
                console.error('Failed to create rematch:', error)
                this.errorReporting.captureError(error, {
                    tags: {
                        component: 'PlayView',
                        action: 'handleRematch',
                        matchId: String(this.$route.params.matchId),
                    },
                    extra: {
                        route: this.$route.path,
                        isSoloGame: this.matchStore.getProfiles.length === 1,
                    },
                })
                this.$toast(this.$t('general.error'), 5000, this.$t('general.error_retry'))
            } finally {
                this.isRematchLoading = false
            }
        },
        handleShowGameStats() {
            this.$router.push({
                name: 'match-game-stats',
                params: { matchId: this.$route.params.matchId },
            })
        },
        async handleNewGame() {
            try {
                // Show full-screen loading overlay before clearing match state,
                // so the user doesn't see the empty game background flash.
                this.isLoadingMatch = true

                await this.matchStore.selectMatchType(
                    'Camel Race',
                    this.authStore,
                    this.$router,
                    this.$toast,
                )
            } catch (error) {
                console.error('Failed to start new Camelrace game:', error)
                this.isLoadingMatch = false
            }
        },
        handleExit() {
            this.matchStore.clearMatch()
            this.$router.push({ name: 'home' })
        },
        showLegWonMessage() {
            this.countdownValue = 3
            this.showLegWonNotification = true
            this.countdownDepth++
            this.matchStore.setRealtimePause(true)

            this.countdownInterval = setInterval(() => {
                this.countdownValue--
                if (this.countdownValue <= 0) {
                    clearInterval(this.countdownInterval)
                    this.countdownInterval = null
                    this.showLegWonNotification = false
                    if (--this.countdownDepth === 0)
                        this.matchStore.flushQueuedRtUpdatesWithSpacing()
                }
            }, 1000)
        },
        showSetWonMessage() {
            const currentSet = this.matchData.sets[this.matchData.sets.length - 2]
            this.setWinnerId = currentSet?.winner_id || null
            this.setCountdownValue = 3
            this.showSetWonNotification = true
            this.countdownDepth++
            this.matchStore.setRealtimePause(true)

            this.setCountdownInterval = setInterval(() => {
                this.setCountdownValue--
                if (this.setCountdownValue <= 0) {
                    clearInterval(this.setCountdownInterval)
                    this.setCountdownInterval = null
                    this.showSetWonNotification = false
                    if (--this.countdownDepth === 0)
                        this.matchStore.flushQueuedRtUpdatesWithSpacing()
                }
            }, 1000)
        },
    },
    beforeRouteLeave(to, _from, next) {
        // Since when we're performing a rematch, we're also managing the channels,
        // we skip the leaveMatchChannel when we're redirecting for a rematch
        if (this.matchStore.activeChannel && !to.query.rematch) {
            this.matchStore.leaveMatchChannel()
        }
        next()
    },
    beforeUnmount() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
            this.countdownInterval = null
        }
        if (this.setCountdownInterval) {
            clearInterval(this.setCountdownInterval)
            this.setCountdownInterval = null
        }
        this.matchStore.clearRtQueueAndCancelFlush()
    },
}
</script>
